import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Image as ImageIcon, User as UserIcon, Calendar, Tag, History, Download } from 'lucide-react';
import { useT } from '@/store/i18n';
import { useAuth } from '@/store/auth';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Switch } from '@/components/ui/Switch';
import { RevisionHistoryModal } from '@/components/RevisionHistoryModal';
import { downloadSingleQuestion } from '@/lib/export';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { Question, AnswerOption } from '@/types';

const TAG_PALETTE = [
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-rose-100 text-rose-700',
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
  'bg-orange-100 text-orange-700',
];

function tagColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
}

function TagBadges({ tags }: { tags: Array<{ id: number; name: string }> }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag) => (
        <span key={tag.id} className={cn('px-2 py-0.5 rounded-full text-xs font-medium', tagColor(tag.name))}>
          {tag.name}
        </span>
      ))}
    </div>
  );
}

const STUDENT_VIEW_KEY = 'baiboon_v8_student_view';

interface Props {
  questionId: number | null;
  onClose: () => void;
}

export function QuestionDetailModal({ questionId, onClose }: Props) {
  const t = useT();
  const role = useAuth((s) => s.user?.role);
  const typeLabel = (type: string) => t(`detail.type.${type}`);
  const [revisionsId, setRevisionsId] = useState<number | null>(null);

  const defaultStudentView = role === 'teacher';
  const [studentView, setStudentView] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STUDENT_VIEW_KEY);
      return stored !== null ? stored === 'true' : defaultStudentView;
    } catch {
      return defaultStudentView;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STUDENT_VIEW_KEY, String(studentView));
    } catch {}
  }, [studentView]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['question-detail', questionId],
    queryFn: async () => {
      const r = await api<{ data?: { question?: Question } & Question } & { question?: Question }>(
        `questions/manage.php?id=${questionId}`
      );
      return r.data?.question ?? r.question ?? (r.data as Question | undefined);
    },
    enabled: !!questionId,
  });

  const q = data as Question | undefined;

  return (
    <Modal
      open={!!questionId}
      onClose={onClose}
      title={t('detail.title')}
      description={q?.topic || ' '}
      size="lg"
      footer={
        q ? (
          <>
            <Button variant="ghost" leftIcon={<History className="w-4 h-4" />} onClick={() => setRevisionsId(q.id)}>
              {t('detail.history_btn')}
            </Button>
            <Button variant="secondary" leftIcon={<Download className="w-4 h-4" />} onClick={() => downloadSingleQuestion(q.id, 'pdf')}>
              {t('detail.download_pdf')}
            </Button>
            <Button onClick={onClose}>{t('btn.close')}</Button>
          </>
        ) : null
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : error ? (
        <div className="text-sm text-red-600">{t('detail.load_fail')}: {(error as Error).message}</div>
      ) : !q ? (
        <div className="text-sm text-slate-500">{t('detail.not_found')}</div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={q.status} />
              {q.subject_name && <Badge tone="brand">{q.subject_name}</Badge>}
              {q.grade_name && <Badge tone="info">{q.grade_name}</Badge>}
              {q.question_type && <Badge tone="neutral">{typeLabel(q.question_type)}</Badge>}
              {q.difficulty_level && <Badge tone="warning">{q.difficulty_level}</Badge>}
            </div>
            <Switch
              label={t('detail.student_view')}
              checked={studentView}
              onChange={(e) => setStudentView(e.target.checked)}
            />
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{t('detail.question_label')}</div>
            <p className="text-base text-slate-900 leading-relaxed whitespace-pre-wrap">
              {q.question_text || t('detail.no_question')}
            </p>
            {q.image_path && (
              <div className="mt-3">
                <img
                  src={`/${q.image_path.replace(/^\.?\//, '')}`}
                  alt={t('detail.image_alt')}
                  loading="lazy"
                  className="max-w-full max-h-80 rounded-lg border border-slate-200"
                />
              </div>
            )}
          </div>

          {/* Options/Answers */}
          {(q.question_type === 'single_choice' || q.question_type === 'multiple_answer') && (
            <Options options={q.options ?? q.answer_options ?? []} studentView={studentView} />
          )}
          {q.question_type === 'true_false' && !studentView && (
            <TrueFalse options={q.options ?? q.answer_options ?? []} />
          )}
          {q.question_type === 'essay' && !studentView && q.model_answer && (
            <EssayAnswer modelAnswer={q.model_answer} scoringCriteria={q.scoring_criteria} />
          )}

          {/* Metadata footer */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-slate-100 text-xs">
            <Meta icon={<UserIcon className="w-3.5 h-3.5" />} label={t('detail.creator_label')} value={q.creator_name ?? q.created_by_name} />
            <Meta icon={<Calendar className="w-3.5 h-3.5" />} label={t('detail.created_label')} value={q.created_at ? new Date(q.created_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'} />
            <Meta icon={<Tag className="w-3.5 h-3.5" />} label={t('detail.points_label')} value={q.points ? `${q.points}` : '—'} />
            <Meta icon={<ImageIcon className="w-3.5 h-3.5" />} label="ID" value={`#${q.id}`} />
          </div>

          {q.learning_indicators && q.learning_indicators.length > 0 && (
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{t('detail.indicators_label')}</div>
              <div className="flex flex-wrap gap-1.5">
                {q.learning_indicators.map((ind) => (
                  <Badge key={ind.id} tone="info">
                    <span className="font-mono text-[10px]">{ind.code}</span> · {ind.title}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {(q as Question & { tags?: Array<{ id: number; name: string }> }).tags?.length ? (
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{t('tag.section_label')}</div>
              <TagBadges tags={(q as Question & { tags?: Array<{ id: number; name: string }> }).tags!} />
            </div>
          ) : null}
        </div>
      )}

      <RevisionHistoryModal questionId={revisionsId} onClose={() => setRevisionsId(null)} />
    </Modal>
  );
}

function Options({ options, studentView }: { options: AnswerOption[]; studentView: boolean }) {
  const t = useT();
  if (options.length === 0) {
    return <div className="text-sm text-slate-500">{t('detail.no_options')}</div>;
  }
  return (
    <div>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{t('detail.options_label')}</div>
      <ul className="space-y-1.5">
        {options.map((o, i) => {
          const correct = !studentView && (o.is_correct == 1 || o.is_correct === true);
          const text = o.text || o.option_text || '';
          return (
            <li
              key={o.id ?? i}
              className={`flex items-start gap-3 px-3 py-2 rounded-lg border ${
                correct ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'
              }`}
            >
              <span
                className={`w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center flex-shrink-0 ${
                  correct ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'
                }`}
              >
                {String.fromCharCode(65 + i)}
              </span>
              <div className="flex-1 text-sm text-slate-800">
                <div className="flex items-center gap-2">
                  {text}
                  {correct && <Check className="w-4 h-4 text-emerald-600" />}
                </div>
                {!studentView && o.explanation && (
                  <div className="text-xs text-slate-500 mt-1">{t('detail.explanation')}: {o.explanation}</div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EssayAnswer({ modelAnswer, scoringCriteria }: { modelAnswer: string; scoringCriteria?: string }) {
  const t = useT();
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{t('wizard.essay_answer_label')}</div>
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-slate-800 whitespace-pre-wrap">
        {modelAnswer}
      </div>
      {scoringCriteria && (
        <>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-2 mb-1">{t('wizard.essay_criteria_label')}</div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-slate-800 whitespace-pre-wrap">
            {scoringCriteria}
          </div>
        </>
      )}
    </div>
  );
}

function TrueFalse({ options }: { options: AnswerOption[] }) {
  const t = useT();
  const correct = options.find((o) => o.is_correct == 1 || o.is_correct === true);
  return (
    <div>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{t('detail.answer_label')}</div>
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
        <span className="font-semibold text-emerald-800">
          {correct ? (correct.text || correct.option_text || t('detail.correct_default')) : t('detail.no_answer')}
        </span>
      </div>
    </div>
  );
}

function Meta({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | null }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-slate-400 mb-0.5">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-slate-700 font-medium truncate">{value || '—'}</div>
    </div>
  );
}
